import type { LatLngBoundsExpression } from 'leaflet';
import { matchCountry } from './countries';

export type Continent = 'EU' | 'AF' | 'AS' | 'NA' | 'SA' | 'OC';

/**
 * The frame a country is drawn in. Usually its continent — but Europe's frame is cropped tight
 * around the populated mainland (see `CONTINENT_BOUNDS`), which leaves a few countries outside
 * it. Rather than loosen the crop for all of Europe, or pin them against an edge where the
 * marker is half cut off, those get a frame of their own.
 */
export type FrameKey = Continent | 'EU_ATLANTIC' | 'EU_EAST' | 'MED_EAST';

/**
 * A country's rough position on the globe: which continent frames it, and one point to pin.
 *
 * The point is an approximate centroid, deliberately not a precise one — it is only ever shown
 * inside a continent-wide thumbnail, where a whole degree is barely a pixel. It answers "which
 * part of the world is this venue in", never "where exactly". A handful of entries use the main
 * landmass or the capital instead of the true centroid, where the centroid would land somewhere
 * unhelpful: Russia is pinned near Moscow and framed with Europe (its geometric centre is in
 * central Siberia, far from anywhere a venue would be), and scattered archipelagos (Kiribati,
 * Samoa, Tonga) are pinned on their main island.
 *
 * Transcontinental countries are filed under the frame that shows them best: Turkey and Cyprus
 * with Europe, the Caucasus with Asia.
 */
export const COUNTRY_GEO: Record<string, readonly [FrameKey, number, number]> = {
  // Europe
  AL: ['EU', 41.1, 20.1], AD: ['EU', 42.5, 1.5], AT: ['EU', 47.6, 14.1], BY: ['EU', 53.7, 27.9],
  BE: ['EU', 50.6, 4.6], BA: ['EU', 44.0, 17.8], BG: ['EU', 42.7, 25.3], HR: ['EU', 45.1, 15.5],
  CY: ['MED_EAST', 35.0, 33.2], CZ: ['EU', 49.8, 15.5], DK: ['EU', 56.0, 9.5], EE: ['EU', 58.7, 25.5],
  FI: ['EU', 61.3, 25.0], FR: ['EU', 46.6, 2.4], DE: ['EU', 51.1, 10.4], GR: ['EU', 39.1, 22.5],
  HU: ['EU', 47.2, 19.4], IS: ['EU_ATLANTIC', 64.9, -18.6], IE: ['EU', 53.2, -8.1], IT: ['EU', 42.8, 12.5],
  XK: ['EU', 42.6, 20.9], LV: ['EU', 56.9, 24.9], LI: ['EU', 47.2, 9.5], LT: ['EU', 55.3, 23.9],
  LU: ['EU', 49.8, 6.1], MT: ['EU', 35.9, 14.4], MD: ['EU', 47.2, 28.5], MC: ['EU', 43.7, 7.4],
  ME: ['EU', 42.8, 19.3], NL: ['EU', 52.2, 5.5], MK: ['EU', 41.6, 21.7], NO: ['EU', 60.8, 9.0],
  PL: ['EU', 52.0, 19.4], PT: ['EU', 39.6, -8.0], RO: ['EU', 45.9, 24.9], RU: ['EU_EAST', 56.0, 38.0],
  SM: ['EU', 43.9, 12.5], RS: ['EU', 44.1, 20.8], SK: ['EU', 48.7, 19.5], SI: ['EU', 46.1, 14.8],
  ES: ['EU', 40.2, -3.6], SE: ['EU', 59.8, 15.2], CH: ['EU', 46.8, 8.2], TR: ['MED_EAST', 39.0, 35.4],
  UA: ['EU', 48.9, 31.2], GB: ['EU', 54.0, -2.5], VA: ['EU', 41.9, 12.45],

  // Asia
  AF: ['AS', 33.8, 66.0], AM: ['AS', 40.3, 44.9], AZ: ['AS', 40.3, 47.7], BH: ['AS', 26.0, 50.5],
  BD: ['AS', 23.9, 90.3], BT: ['AS', 27.4, 90.4], BN: ['AS', 4.5, 114.7], KH: ['AS', 12.7, 104.9],
  CN: ['AS', 35.0, 103.0], GE: ['AS', 42.2, 43.5], IN: ['AS', 22.9, 79.6], ID: ['AS', -2.2, 117.0],
  IR: ['AS', 32.6, 54.3], IQ: ['AS', 33.1, 43.7], IL: ['AS', 31.4, 35.0], JP: ['AS', 36.5, 138.2],
  JO: ['AS', 31.2, 36.8], KZ: ['AS', 48.2, 67.3], KP: ['AS', 40.2, 127.2], KR: ['AS', 36.4, 127.9],
  KW: ['AS', 29.3, 47.6], KG: ['AS', 41.5, 74.6], LA: ['AS', 18.5, 103.8], LB: ['AS', 33.9, 35.9],
  MY: ['AS', 4.2, 109.5], MV: ['AS', 3.2, 73.2], MN: ['AS', 46.8, 103.1], MM: ['AS', 21.2, 96.5],
  NP: ['AS', 28.3, 84.1], OM: ['AS', 20.6, 56.1], PK: ['AS', 30.0, 69.3], PS: ['AS', 31.9, 35.2],
  PH: ['AS', 12.0, 122.0], QA: ['AS', 25.3, 51.2], SA: ['AS', 24.1, 44.5], SG: ['AS', 1.35, 103.8],
  LK: ['AS', 7.6, 80.7], SY: ['AS', 35.0, 38.5], TW: ['AS', 23.7, 120.9], TJ: ['AS', 38.5, 71.0],
  TH: ['AS', 15.1, 101.0], TL: ['AS', -8.8, 125.9], AE: ['AS', 24.0, 54.3], UZ: ['AS', 41.8, 63.1],
  TM: ['AS', 39.1, 59.2], VN: ['AS', 16.6, 106.3], YE: ['AS', 15.9, 47.6],

  // Africa
  DZ: ['AF', 28.2, 2.6], AO: ['AF', -12.3, 17.5], BJ: ['AF', 9.6, 2.3], BW: ['AF', -22.2, 23.8],
  BF: ['AF', 12.3, -1.7], BI: ['AF', -3.4, 29.9], CV: ['AF', 16.0, -24.0], CM: ['AF', 5.7, 12.7],
  CF: ['AF', 6.6, 20.5], TD: ['AF', 15.4, 18.7], KM: ['AF', -11.8, 43.4], CG: ['AF', -0.8, 15.2],
  CD: ['AF', -2.9, 23.6], CI: ['AF', 7.6, -5.6], DJ: ['AF', 11.8, 42.6], EG: ['AF', 26.5, 29.9],
  GQ: ['AF', 1.6, 10.5], ER: ['AF', 15.4, 38.9], SZ: ['AF', -26.6, 31.5], ET: ['AF', 8.6, 39.6],
  GA: ['AF', -0.6, 11.8], GM: ['AF', 13.4, -15.4], GH: ['AF', 7.9, -1.2], GN: ['AF', 10.4, -10.9],
  GW: ['AF', 12.0, -15.0], KE: ['AF', 0.6, 37.9], LS: ['AF', -29.6, 28.2], LR: ['AF', 6.4, -9.3],
  LY: ['AF', 27.0, 18.0], MG: ['AF', -19.4, 46.7], MW: ['AF', -13.2, 34.3], ML: ['AF', 17.3, -3.5],
  MR: ['AF', 20.3, -10.4], MU: ['AF', -20.3, 57.6], MA: ['AF', 31.9, -6.9], MZ: ['AF', -17.3, 35.5],
  NA: ['AF', -22.1, 17.2], NE: ['AF', 17.4, 9.4], NG: ['AF', 9.6, 8.1], RW: ['AF', -2.0, 29.9],
  ST: ['AF', 0.4, 6.7], SN: ['AF', 14.4, -14.5], SC: ['AF', -4.7, 55.5], SL: ['AF', 8.6, -11.8],
  SO: ['AF', 4.8, 45.7], ZA: ['AF', -29.0, 25.1], SS: ['AF', 7.3, 30.3], SD: ['AF', 15.9, 29.9],
  TZ: ['AF', -6.3, 34.8], TG: ['AF', 8.6, 1.0], TN: ['AF', 34.1, 9.6], UG: ['AF', 1.3, 32.4],
  ZM: ['AF', -13.5, 27.8], ZW: ['AF', -19.0, 29.9],

  // North & Central America, Caribbean
  AG: ['NA', 17.1, -61.8], BS: ['NA', 24.5, -77.5], BB: ['NA', 13.2, -59.5], BZ: ['NA', 17.2, -88.6],
  CA: ['NA', 58.0, -101.0], CR: ['NA', 9.9, -84.0], CU: ['NA', 21.6, -79.5], DM: ['NA', 15.4, -61.4],
  DO: ['NA', 18.8, -70.4], SV: ['NA', 13.8, -88.9], GD: ['NA', 12.1, -61.7], GT: ['NA', 15.5, -90.3],
  HT: ['NA', 19.0, -72.8], HN: ['NA', 14.8, -86.6], JM: ['NA', 18.1, -77.3], MX: ['NA', 23.6, -102.5],
  NI: ['NA', 12.9, -85.0], PA: ['NA', 8.5, -80.1], KN: ['NA', 17.3, -62.7], LC: ['NA', 13.9, -61.0],
  VC: ['NA', 13.25, -61.2], TT: ['NA', 10.5, -61.3], US: ['NA', 39.4, -98.6],

  // South America
  AR: ['SA', -35.4, -65.2], BO: ['SA', -16.7, -64.7], BR: ['SA', -10.8, -52.9], CL: ['SA', -35.7, -71.4],
  CO: ['SA', 4.1, -73.1], EC: ['SA', -1.4, -78.4], GY: ['SA', 4.8, -58.9], PY: ['SA', -23.2, -58.2],
  PE: ['SA', -9.2, -75.0], SR: ['SA', 4.1, -56.0], UY: ['SA', -32.8, -56.0], VE: ['SA', 7.1, -66.1],

  // Oceania
  AS: ['OC', -14.3, -170.7], AU: ['OC', -25.7, 134.5], FJ: ['OC', -17.8, 178.0], KI: ['OC', 1.4, 173.0],
  MH: ['OC', 7.1, 171.2], FM: ['OC', 6.9, 158.2], NR: ['OC', -0.53, 166.9], NZ: ['OC', -41.5, 172.8],
  PW: ['OC', 7.5, 134.6], PG: ['OC', -6.5, 145.2], WS: ['OC', -13.8, -172.1], SB: ['OC', -9.0, 159.3],
  TO: ['OC', -21.2, -175.2], TV: ['OC', -8.5, 179.2], VU: ['OC', -16.3, 167.5],
};

/**
 * The frame each key is shown in — generous enough that no country's pin above can fall outside
 * its own frame, which is the property that makes the thumbnail readable at all.
 *
 * Europe is the one frame cropped deliberately tight rather than generously: the continent's
 * outliers (Iceland, Lapland, the eastern Mediterranean) are so far apart that framing all of
 * them leaves the part anyone actually books in — the mainland — small and adrift. So the box
 * stops at Vaasa in the north, short of Iceland in the west and short of Israel in the
 * south-east, which buys roughly 1.5× the scale. It is also cut square in Mercator, so the
 * fitted view is the box itself: a non-square box would give back the crop as slack.
 * The four countries this leaves out (IS, RU, TR, CY) are drawn in the three frames below.
 *
 * Oceania's east edge runs past the antimeridian (190 = 170°W) because the region straddles it;
 * `normalizeLongitude` below shifts pins east of it into the same range, or Leaflet would place
 * Samoa and Tonga on the far side of the world from the rest of the frame.
 */
export const CONTINENT_BOUNDS: Record<FrameKey, LatLngBoundsExpression> = {
  EU: [[34.4, -10.5], [63.1, 34.8]],
  AF: [[-36, -29], [38, 63]],
  AS: [[-11, 25], [56, 147]],
  NA: [[7, -168], [72, -52]],
  SA: [[-56, -82], [13, -34]],
  OC: [[-48, 110], [22, 193]],
  // Iceland, with the British Isles and Scandinavia for bearing.
  EU_ATLANTIC: [[48, -27], [68, 16]],
  // Russia end to end, with eastern Europe at its western edge.
  EU_EAST: [[41, 19], [70, 140]],
  // The eastern Mediterranean: Greece across to the Caucasus and the Levant.
  MED_EAST: [[30, 19], [45, 45]],
};

export interface CountryGeo {
  code: string;
  /** The canonical country name, for labels — the stored string may be a code or an alias. */
  name: string;
  /** The frame this country is drawn in — its continent, or one of the three exceptions. */
  frame: FrameKey;
  latitude: number;
  longitude: number;
  bounds: LatLngBoundsExpression;
}

/** Resolve a stored country string (code, name or alias) to its frame and pin. */
export function getCountryGeo(country: string | undefined | null): CountryGeo | undefined {
  if (!country) return undefined;
  const matched = matchCountry(country);
  const entry = matched ? COUNTRY_GEO[matched.code] : undefined;
  if (!matched || !entry) return undefined;
  const [frame, latitude, longitude] = entry;
  return { code: matched.code, name: matched.name, frame, latitude, longitude, bounds: CONTINENT_BOUNDS[frame] };
}

/**
 * Bring a longitude into the same world copy as a frame whose east edge runs past 180°.
 * Leaflet projects marker longitudes literally, so a Samoan pin at -172 would otherwise land
 * a world away from an Oceania view spanning 110 → 190.
 */
export function normalizeLongitude(longitude: number, bounds: LatLngBoundsExpression): number {
  const east = (bounds as [[number, number], [number, number]])[1][1];
  return east > 180 && longitude < east - 360 ? longitude + 360 : longitude;
}
